import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchChatbots } from "../redux/store/chatbotSlice";
import { getUserId } from "../utils/auth";
import usePageLoader from "../hooks/usePageLoader";
import {
  FiPlus, FiSettings, FiCopy, FiBarChart2, FiMessageCircle,
  FiUsers, FiGrid, FiZap,
} from "react-icons/fi";
import toast from "react-hot-toast";

/* ── Per-bot mock stats ── */
const BOT_STATS = {
  "bot-001": { conversations: 347, messages: 2940, leads: 15, training: 85 },
  "bot-002": { conversations: 189, messages: 1450, leads: 8,  training: 72 },
  "bot-003": { conversations: 92,  messages: 680,  leads: 4,  training: 60 },
};

const CARD_GRADIENTS = [
  "from-violet-500 to-indigo-700",
  "from-blue-500 to-indigo-600",
  "from-emerald-500 to-teal-600",
  "from-orange-400 to-rose-500",
];

const PLAN_LIMIT = 10;

function ChatbotsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm animate-pulse">
          <div className="h-24 bg-gray-200" />
          <div className="px-5 -mt-5 relative z-10">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 mb-4">
              <div className="h-4 bg-gray-200 rounded w-3/5 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/5" />
            </div>
            <div className="grid grid-cols-3 gap-2.5 mb-4">
              {[1, 2, 3].map((j) => (
                <div key={j} className="bg-gray-100 rounded-xl h-14" />
              ))}
            </div>
            <div className="mb-5 space-y-1.5">
              <div className="flex justify-between">
                <div className="h-3 bg-gray-100 rounded w-16" />
                <div className="h-3 bg-gray-100 rounded w-8" />
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full w-full" />
            </div>
            <div className="flex gap-2 pb-5">
              <div className="flex-1 h-9 bg-gray-200 rounded-lg" />
              <div className="w-9 h-9 bg-gray-100 rounded-lg" />
              <div className="w-9 h-9 bg-gray-100 rounded-lg" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ChatbotsList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { chatbots, loading: apiLoading } = useSelector((state) => state.chatbots);
  const pageLoading = usePageLoader();
  const loading = apiLoading || pageLoading;
  const userId = getUserId();

  useEffect(() => {
    if (!chatbots.length) dispatch(fetchChatbots(userId));
  }, [dispatch, chatbots.length, userId]);

  const copyLink = (uniqueId) => {
    navigator.clipboard.writeText(`https://app.nexagpt.ai/embed/${uniqueId}`);
    toast.success("Chatbot link copied!");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">

      {/* ── Page Header ── */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Your Chatbots</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage, monitor, and deploy your AI chatbots.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Plan usage pill */}
          <div className="hidden sm:flex flex-col items-end gap-1">
            <p className="text-xs text-gray-500 font-medium">
              {chatbots.length} / {PLAN_LIMIT} chatbots
            </p>
            <div className="w-32 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${(chatbots.length / PLAN_LIMIT) * 100}%` }}
              />
            </div>
          </div>
          <button
            onClick={() => navigate("/chatbot/create")}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition shadow-sm shadow-primary/30"
          >
            <FiPlus size={15} /> New Chatbot
          </button>
        </div>
      </div>

      {/* ── Loading skeletons ── */}
      {loading && <ChatbotsSkeleton />}

      {/* ── Card Grid ── */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

          {chatbots.map((bot, idx) => {
            const stats    = { conversations: bot._count?.conversations ?? 0, messages: 0, leads: bot._count?.leads ?? 0, training: 0 };
            const gradient = CARD_GRADIENTS[idx % CARD_GRADIENTS.length];

            return (
              <div
                key={bot.id}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Card gradient header */}
                <div className={`relative bg-gradient-to-br ${gradient} px-5 pt-5 pb-8`}>
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl shadow-sm">
                      {bot.icon || "🤖"}
                    </div>
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold bg-white/20 text-white px-2.5 py-1 rounded-full backdrop-blur-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                      Active
                    </span>
                  </div>
                </div>

                {/* Card body — pulled up over the gradient */}
                <div className="px-5 -mt-5 flex flex-col flex-1 relative z-10">

                  {/* Name card */}
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 mb-4">
                    <p className="font-bold text-gray-900 text-base truncate">{bot.name}</p>
                    <p className="text-[11px] text-gray-400 font-mono mt-0.5">{bot.uniqueId}</p>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2.5 mb-4">
                    <StatTile icon={FiBarChart2}    label="Convos"   value={stats.conversations} color="text-violet-500" bg="bg-violet-50" />
                    <StatTile icon={FiMessageCircle} label="Messages" value={stats.messages}      color="text-blue-500"  bg="bg-blue-50" />
                    <StatTile icon={FiUsers}         label="Leads"    value={stats.leads}          color="text-emerald-500" bg="bg-emerald-50" />
                  </div>

                  {/* Training progress */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] text-gray-500 font-medium flex items-center gap-1">
                        <FiZap size={10} className="text-yellow-500" /> Training
                      </span>
                      <span className="text-[11px] font-semibold text-gray-700">{stats.training}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-indigo-400 rounded-full transition-all"
                        style={{ width: `${stats.training}%` }}
                      />
                    </div>
                  </div>

                  {/* Spacer pushes actions to bottom */}
                  <div className="flex-1" />

                  {/* Actions */}
                  <div className="flex items-center gap-2 pb-5">
                    <button
                      onClick={() => navigate(`/overview/${bot.id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:opacity-90 transition"
                    >
                      <FiGrid size={12} /> Dashboard
                    </button>
                    <button
                      onClick={() => navigate(`/advanced/settings/${bot.id}`)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition"
                      title="Settings"
                    >
                      <FiSettings size={14} />
                    </button>
                    <button
                      onClick={() => copyLink(bot.uniqueId)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition"
                      title="Copy embed link"
                    >
                      <FiCopy size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Create-new slot card ── */}
          {chatbots.length < PLAN_LIMIT && (
            <button
              onClick={() => navigate("/chatbot/create")}
              className="border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-3 py-12 px-6 text-gray-400 hover:border-primary hover:text-primary hover:bg-primary-light/30 transition-all group"
            >
              <div className="w-12 h-12 rounded-2xl border-2 border-dashed border-gray-300 group-hover:border-primary flex items-center justify-center transition">
                <FiPlus size={20} />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold">New Chatbot</p>
                <p className="text-xs mt-0.5 text-gray-400">
                  {PLAN_LIMIT - chatbots.length} slot{PLAN_LIMIT - chatbots.length !== 1 ? "s" : ""} remaining
                </p>
              </div>
            </button>
          )}

        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && chatbots.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary-light flex items-center justify-center text-3xl">
            🤖
          </div>
          <div>
            <p className="text-lg font-semibold text-gray-900">No chatbots yet</p>
            <p className="text-sm text-gray-500 mt-1">Create your first chatbot and go live in minutes.</p>
          </div>
          <button
            onClick={() => navigate("/chatbot/create")}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition shadow-sm shadow-primary/30"
          >
            <FiPlus size={15} /> Create First Chatbot
          </button>
        </div>
      )}

    </div>
  );
}

function StatTile({ icon: Icon, label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-xl px-3 py-2.5 flex flex-col gap-1`}>
      <Icon size={13} className={color} />
      <p className="text-sm font-bold text-gray-900 leading-none">
        {value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
      </p>
      <p className="text-[10px] text-gray-400 font-medium">{label}</p>
    </div>
  );
}
